import * as React from "react";

import Layout from "../components/layout";
import Meta from "../components/meta";
import { OutboundLink } from "gatsby-plugin-google-analytics";

export default function Legal() {
  return (
    <Layout>
      <Meta title="Legal" />
      <h2>Privacy Policy</h2>
      <h3>Summary</h3>
      <ul>
        <li>
          We store app lists for analytical purposes, such as creating the "top
          beta apps" section (with a timestamp).
        </li>
        <li>We track general analytics to measure app engagement.</li>
        <li>We do not store any other personally identifiable information.</li>
      </ul>

      <h3>Full Policy</h3>

      <p>Effective date: April 12, 2019</p>

      <p>
        Beta Lister ("us", "we", or "our") operates the Beta Lister mobile
        application (the "Service").
      </p>

      <p>
        This page informs you of our policies regarding the collection, use, and
        disclosure of personal data when you use our Service and the choices you
        have associated with that data.
      </p>

      <p>
        We use your data to provide and improve the Service. By using the
        Service, you agree to the collection and use of information in
        accordance with this policy. Unless otherwise defined in this Privacy
        Policy, terms used in this Privacy Policy have the same meanings as in
        our Terms and Conditions.
      </p>

      <h4>Information Collection And Use</h4>

      <p>
        We collect several different types of information for various purposes
        to provide and improve our Service to you.
      </p>

      <h5>Types of Data Collected</h5>

      <h6>Personal Data</h6>

      <p>
        While using our Service, we may ask you to provide us with certain
        personally identifiable information that can be used to contact or
        identify you ("Personal Data"). Personally identifiable information may
        include, but is not limited to:
      </p>

      <ul>
        <li>Cookies and Usage Data</li>
      </ul>

      <h6>Usage Data</h6>

      <p>
        When you access the Service by or through a mobile device, we may
        collect certain information automatically, including, but not limited
        to, the type of mobile device you use, your mobile device unique ID, the
        IP address of your mobile device, your mobile operating system, the type
        of mobile Internet browser you use, unique device identifiers and other
        diagnostic data ("Usage Data").
      </p>

      <p>
        The Service stores the list of applications installed on your mobile
        device, with no other personally identifiable information attached to
        this data.
      </p>

      <h4>Use of Data</h4>

      <p>Beta Lister uses the collected data for various purposes:</p>
      <ul>
        <li>To provide and maintain the Service</li>
        <li>To notify you about changes to our Service</li>
        <li>
          To allow you to participate in interactive features of our Service
          when you choose to do so
        </li>
        <li>To provide customer care and support</li>
        <li>
          To provide analysis or valuable information so that we can improve the
          Service
        </li>
        <li>To monitor the usage of the Service</li>
        <li>To detect, prevent and address technical issues</li>
        <li>To generate public app lists</li>
      </ul>

      <h4>Transfer Of Data</h4>
      <p>
        Your information, including Personal Data, may be transferred to — and
        maintained on — computers located outside of your state, province,
        country or other governmental jurisdiction where the data protection
        laws may differ than those from your jurisdiction.
      </p>
      <p>
        If you are located outside Canada and choose to provide information to
        us, please note that we transfer the data, including Personal Data, to
        Canada and process it there.
      </p>
      <p>
        Your consent to this Privacy Policy followed by your submission of such
        information represents your agreement to that transfer.
      </p>
      <p>
        Beta Lister will take all steps reasonably necessary to ensure that your
        data is treated securely and in accordance with this Privacy Policy and
        no transfer of your Personal Data will take place to an organization or
        a country unless there are adequate controls in place including the
        security of your data and other personal information.
      </p>

      <h4>Disclosure Of Data</h4>

      <h5>Legal Requirements</h5>
      <p>
        Beta Lister may disclose your Personal Data in the good faith belief
        that such action is necessary to:
      </p>
      <ul>
        <li>To comply with a legal obligation</li>
        <li>To protect and defend the rights or property of Beta Lister</li>
        <li>
          To prevent or investigate possible wrongdoing in connection with the
          Service
        </li>
        <li>
          To protect the personal safety of users of the Service or the public
        </li>
        <li>To protect against legal liability</li>
      </ul>

      <h4>Security Of Data</h4>
      <p>
        The security of your data is important to us, but remember that no
        method of transmission over the Internet, or method of electronic
        storage is 100% secure. While we strive to use commercially acceptable
        means to protect your Personal Data, we cannot guarantee its absolute
        security.
      </p>

      <h4>Service Providers</h4>
      <p>
        We may employ third party companies and individuals to facilitate our
        Service ("Service Providers"), to provide the Service on our behalf, to
        perform Service-related services or to assist us in analyzing how our
        Service is used.
      </p>
      <p>
        These third parties have access to your Personal Data only to perform
        these tasks on our behalf and are obligated not to disclose or use it
        for any other purpose.
      </p>

      <h5>Analytics</h5>
      <p>
        We may use third-party Service Providers to monitor and analyze the use
        of our Service.
      </p>
      <ul>
        <li>
          <p>
            <strong>Firebase</strong>
          </p>
          <p>Firebase is analytics service provided by Google Inc.</p>
          <p>
            You may opt-out of certain Firebase features through your mobile
            device settings, such as your device advertising settings or by
            following the instructions provided by Google in their{" "}
            <OutboundLink href="https://policies.google.com/privacy?hl=en">
              Privacy Policy
            </OutboundLink>
            .
          </p>
          <p>
            We also encourage you to review the{" "}
            <OutboundLink href="https://support.google.com/analytics/answer/6004245">
              Google's policy
            </OutboundLink>{" "}
            for safeguarding your data. For more information on what type of
            information Firebase collects, please visit please visit the{" "}
            <OutboundLink href="https://policies.google.com/privacy?hl=en">
              Google Privacy & Terms
            </OutboundLink>{" "}
            web page.
          </p>
        </li>
      </ul>

      <h4>Links To Other Sites</h4>
      <p>
        Our Service may contain links to other sites that are not operated by
        us. If you click on a third party link, you will be directed to that
        third party's site. We strongly advise you to review the Privacy Policy
        of every site you visit.
      </p>
      <p>
        We have no control over and assume no responsibility for the content,
        privacy policies or practices of any third party sites or services.
      </p>

      <h4>Children's Privacy</h4>
      <p>
        Our Service does not address anyone under the age of 18 ("Children").
      </p>
      <p>
        We do not knowingly collect personally identifiable information from
        anyone under the age of 18. If you are a parent or guardian and you are
        aware that your Children has provided us with Personal Data, please
        contact us. If we become aware that we have collected Personal Data from
        children without verification of parental consent, we take steps to
        remove that information from our servers.
      </p>

      <h4>Changes To This Privacy Policy</h4>
      <p>
        We may update our Privacy Policy from time to time. We will notify you
        of any changes by posting the new Privacy Policy on this page.
      </p>
      <p>
        We will let you know via email and/or a prominent notice on our Service,
        prior to the change becoming effective and update the "effective date"
        at the top of this Privacy Policy.
      </p>
      <p>
        You are advised to review this Privacy Policy periodically for any
        changes. Changes to this Privacy Policy are effective when they are
        posted on this page.
      </p>

      <h4>Contact Us</h4>
      <p>
        If you have any questions about this Privacy Policy, please contact us:
      </p>
      <ul>
        <li>
          By email:{" "}
          <OutboundLink href="mailto:support@betalister.app">
            support@betalister.app
          </OutboundLink>
        </li>
      </ul>
    </Layout>
  );
}
